import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { interactionsApi } from '@/lib/api';
import { Combobox, Field, INTERAKTION, NativeSelect, SegmentedControl, formatDatum, toast } from '@elbklar/ui';
import { TextareaField, TextField } from '@/components/form/Felder';
import { toDatetimeLocal } from '@/features/customers/components/FollowUpModal';
import { formatApiError } from '@/lib/validation';
import { useAuswahlSuche, type EntityType } from '@/hooks/useAuswahlSuche';
import type { InteractionCreate, InteractionType } from '@/types';
import { WiedervorlageFeld, followUpDateAus, wiedervorlageAus, type Wiedervorlage } from './WiedervorlageFeld';

// Typen, die das Formular anbietet (InteractionType im Backend ohne Angebot und
// Rechnung, die kommen aus anderen Wegen). Text aus INTERAKTION des Design Systems.
const TYPEN: InteractionType[] = ['call', 'email', 'meeting', 'note', 'task', 'site_visit', 'other'];
const typeOptions = TYPEN.map((value) => ({ value, label: INTERAKTION[value]?.label ?? value }));

/** Betreff aus der ersten Zeile eines Texts, höchstens 80 Zeichen. */
function betreffAus(text: string): string {
  const zeile = text.trim().split('\n')[0].trim();
  return zeile.length > 80 ? `${zeile.slice(0, 79)}…` : zeile;
}

/** Kunde oder Partner, gesucht auf dem Server. Pflicht, darum ohne Entfernen-Kreuz. */
function EntityAuswahl({ art, value, onChange, error }: { art: EntityType; value: string; onChange: (id: string) => void; error?: string }) {
  const suche = useAuswahlSuche(art, value);
  const name = art === 'customer' ? 'Kunde' : 'Partner';
  return (
    <Combobox
      {...suche}
      label={name}
      required
      value={value}
      onChange={onChange}
      error={error}
      placeholder={`${name} wählen`}
      searchPlaceholder={`${name} suchen`}
      emptyText="Keine Treffer"
    />
  );
}

export interface InteractionFormProps {
  /** ID des <form>; der Knopf zum Speichern steht im Fuß des Sheets (`<Button form={formId}>`). */
  formId: string;
  customerId?: string;
  partnerId?: string;
  contactId?: string;
  /** Typ fest vorgeben, z. B. aus Schnell erfassen (Notiz, Anruf): ohne Typauswahl. */
  typ?: InteractionType;
  /**
   * Kurzform fürs Handy (Schnell erfassen): Kunde, falls keiner vorgegeben ist,
   * und ein Text. Der Betreff ist die erste Zeile, der Zeitpunkt jetzt.
   */
  kompakt?: boolean;
  onSuccess?: () => void;
  /** Meldet, ob gerade gespeichert wird (Ladezustand des Knopfs im Fuß). */
  onPendingChange?: (pending: boolean) => void;
  showEntitySelector?: boolean;
  interaction?: {
    id: string;
    type: InteractionType;
    subject: string;
    description: string;
    occurred_at: string;
    /** Gesetzte Wiedervorlage; beim Bearbeiten vorbelegt, entfernen setzt sie auf null. */
    follow_up_date?: string | null;
  };
}

interface FormData extends Omit<InteractionCreate, 'occurred_at'> {
  date: string;
  entityType?: EntityType;
  entityId?: string;
}

export function InteractionForm({
  formId,
  customerId,
  partnerId,
  contactId,
  typ,
  kompakt = false,
  onSuccess,
  onPendingChange,
  showEntitySelector = false,
  interaction,
}: InteractionFormProps) {
  const isEdit = !!interaction;
  const queryClient = useQueryClient();
  // Die Wiedervorlage geht als follow_up_date mit der Interaktion; die Aufgabe
  // „Nachfassen“ legt das Backend in derselben Transaktion an.
  const wiedervorlageVorher = wiedervorlageAus(interaction?.follow_up_date);
  const [wiedervorlage, setWiedervorlage] = useState<Wiedervorlage>(wiedervorlageVorher);
  const [wiedervorlageFehler, setWiedervorlageFehler] = useState<string>();

  // Ohne Kunde und Partner wählt man in der Kurzform den Kunden selbst.
  const mitAuswahl = showEntitySelector || (kompakt && !customerId && !partnerId);
  const initialEntityType: EntityType = partnerId ? 'partner' : 'customer';
  const initialEntityId = partnerId || customerId;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      type: typ || interaction?.type || 'note',
      subject: interaction?.subject || '',
      description: interaction?.description || '',
      // datetime-local will Ortszeit. toISOString().slice(0, 16) gab UTC, das
      // Feld las es als Ortszeit: Jedes Speichern verschob die Interaktion um
      // eine bis zwei Stunden in die Vergangenheit.
      date: toDatetimeLocal(interaction?.occurred_at ?? new Date().toISOString()),
      customer_id: customerId,
      partner_id: partnerId,
      contact_id: contactId,
      entityType: initialEntityType,
      entityId: initialEntityId,
    },
  });

  const entityType = watch('entityType');

  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const payload: InteractionCreate = {
        type: data.type,
        subject: kompakt ? betreffAus(data.description || '') : data.subject,
        description: data.description,
        occurred_at: kompakt ? new Date().toISOString() : new Date(data.date).toISOString(),
        contact_id: data.contact_id,
        customer_id: mitAuswahl
          ? (data.entityType === 'customer' ? data.entityId : undefined)
          : data.customer_id,
        partner_id: mitAuswahl
          ? (data.entityType === 'partner' ? data.entityId : undefined)
          : data.partner_id,
      };
      // Nur senden, was sich geändert hat: null entfernt die Wiedervorlage (die
      // Aufgabe wird abgebrochen). Öffnet jemand das Formular, ohne die
      // bestehende Wiedervorlage mitzugeben, bleibt sie so unangetastet.
      if (wiedervorlage !== wiedervorlageVorher) {
        payload.follow_up_date = wiedervorlage ? followUpDateAus(wiedervorlage) : null;
      }

      return isEdit && interaction
        ? interactionsApi.update(interaction.id, payload)
        : interactionsApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactions'] });
      queryClient.invalidateQueries({ queryKey: ['activity-feed'] });
      // Letzte Interaktion am Kunden, die Aufgabe der Wiedervorlage und die Übersicht ändern sich mit.
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      const art = INTERAKTION[typ ?? '']?.label;
      const gespeichert = !isEdit && art ? `${art} gespeichert` : 'Interaktion gespeichert';
      toast.success(
        wiedervorlage && wiedervorlage !== wiedervorlageVorher
          ? `${gespeichert}, Wiedervorlage am ${formatDatum(wiedervorlage)}`
          : !wiedervorlage && wiedervorlageVorher
            ? `${gespeichert}, Wiedervorlage entfernt`
            : gespeichert,
      );
      onSuccess?.();
    },
    onError: (err) => {
      toast.error(formatApiError(err) || 'Interaktion ließ sich nicht speichern');
    },
  });

  useEffect(() => {
    onPendingChange?.(createMutation.isPending);
  }, [createMutation.isPending, onPendingChange]);

  const onSubmit = (data: FormData) => {
    if (wiedervorlage === '') return;
    createMutation.mutate(data);
  };

  // Geprüft wird nur hier, mit Fehlertext am Feld. Die Formulare tragen
  // noValidate: `required` setzen TextField und Field aus @elbklar/ui auch als
  // Attribut (gut für Screenreader), ohne noValidate hielte der Browser das
  // Absenden mit seiner eigenen Blase auf („Please fill out this field“),
  // bevor ein Fehlertext erscheint. Die Wiedervorlage wird mitgeprüft,
  // damit alle Fehler zugleich stehen.
  const absenden = (e: React.FormEvent<HTMLFormElement>) => {
    setWiedervorlageFehler(wiedervorlage === '' ? 'Datum fehlt' : undefined);
    return handleSubmit(onSubmit)(e);
  };

  const wiedervorlageFeld = (
    <WiedervorlageFeld
      value={wiedervorlage}
      onChange={(wert) => {
        setWiedervorlage(wert);
        setWiedervorlageFehler(undefined);
      }}
      kompakt={kompakt}
      error={wiedervorlageFehler}
    />
  );

  const auswahl = mitAuswahl && (
    <div className="space-y-3">
      {showEntitySelector && (
        <div className="flex flex-col gap-1.5">
          <span className="t-body-strong">
            Zuordnung
            <span className="ml-1 text-muted-foreground" aria-hidden>
              *
            </span>
          </span>
          <SegmentedControl
            label="Zuordnung"
            value={entityType || 'customer'}
            onChange={(v) => {
              setValue('entityType', v as EntityType);
              setValue('entityId', '');
            }}
            items={[
              { value: 'customer', label: 'Kunde' },
              { value: 'partner', label: 'Partner' },
            ]}
          />
        </div>
      )}
      <Controller
        name="entityId"
        control={control}
        rules={{ required: showEntitySelector ? 'Kunde oder Partner fehlt' : 'Kunde fehlt' }}
        render={({ field }) => (
          <EntityAuswahl art={entityType || 'customer'} value={field.value || ''} onChange={field.onChange} error={errors.entityId?.message} />
        )}
      />
    </div>
  );

  if (kompakt) {
    return (
      <form id={formId} onSubmit={absenden} noValidate className="space-y-4">
        {auswahl}
        <Controller
          name="description"
          control={control}
          rules={{ validate: (v) => (v && v.trim() ? true : 'Text fehlt') }}
          render={({ field }) => (
            <TextareaField
              {...field}
              value={field.value ?? ''}
              label={typ === 'call' ? 'Notiz zum Anruf' : 'Notiz'}
              required
              rows={5}
              error={errors.description?.message}
              hint="Die erste Zeile wird zum Betreff."
              placeholder={typ === 'call' ? 'Worum ging es, was ist vereinbart' : 'Was festgehalten werden soll'}
            />
          )}
        />
        {wiedervorlageFeld}
      </form>
    );
  }

  return (
    <form id={formId} onSubmit={absenden} noValidate className="space-y-4">
      {!typ && (
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Field label="Typ">{(f) => <NativeSelect {...f} {...field} options={typeOptions} />}</Field>
          )}
        />
      )}

      {auswahl}

      <Controller
        name="subject"
        control={control}
        rules={{ required: 'Betreff fehlt' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Betreff"
            required
            error={errors.subject?.message}
          />
        )}
      />

      <Controller
        name="date"
        control={control}
        rules={{ required: 'Zeitpunkt fehlt' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Datum und Uhrzeit"
            type="datetime-local"
            required
            error={errors.date?.message}
          />
        )}
      />

      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <TextareaField {...field} value={field.value ?? ''} label="Beschreibung" rows={4} placeholder="Details zur Interaktion" />
        )}
      />

      <div className="border-t border-border pt-4">{wiedervorlageFeld}</div>
    </form>
  );
}
